import React, { useState, useMemo } from 'react';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import dayjs from "dayjs";

import {createTimeTableForLodging} from "../Helpers/EventTimeTableConversion";
import Activity from "../../MasterEventObjects/Activity";
import Idea from "../../MasterEventObjects/Idea";
import Meal from "../../MasterEventObjects/Meal";
import Lodging from "../../MasterEventObjects/Lodging";

// Icons
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HotelIcon from '@mui/icons-material/Hotel';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import HistoryIcon from '@mui/icons-material/History';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';



export default function MasterLists({
                                        open,
                                        onClose,
                                        allEvents,
                                        PA_GlobalObject,
                                        set_PA_GlobalObject,
                                        Account,
                                        sendSocketMessage}) {

    const [currentTab, setCurrentTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    // Drill tracking states
    const [selectedStateFacet, setSelectedStateFacet] = useState(null);
    const [selectedCityFacet, setSelectedCityFacet] = useState(null);

    const tabKeys = ['lodging', 'meals', 'activities', 'ideas'];

    const formatEventDateRange = (timeTable) => {
        if (!timeTable || !timeTable.dates || timeTable.dates.length === 0) return 'No dates set';
        const beginDate = dayjs(timeTable.dates[0].date);
        const endDate = dayjs(timeTable.dates[timeTable.dates.length - 1].date);

        if (beginDate.isSame(endDate, 'day')) {
            return beginDate.format('MMM D, YYYY');
        } else if (beginDate.isSame(endDate, 'year')) {
            return `${beginDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`;
        } else {
            return `${beginDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
        }
    };

    const normalizeAddressString = (str) => {
        if (!str) return '';
        return str
            .toLowerCase()
            .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
            .replace(/\broad\b/g, "rd")
            .replace(/\bstreet\b/g, "st")
            .replace(/\bavenue\b/g, "ave")
            .replace(/\bboulevard\b/g, "blvd")
            .replace(/\bdrive\b/g, "dr")
            .replace(/\blane\b/g, "ln")
            .replace(/\bcourt\b/g, "ct")
            .replace(/\bhighway\b/g, "hwy")
            .replace(/\s+/g, '');
    };

    const getBetterName = (currentName, newName) => {
        if (!currentName) return newName;
        if (!newName) return currentName;

        const getScore = (name) => {
            const lower = name.toLowerCase();
            if (lower.includes('at event location') || lower.includes('see below')) return 1;
            if (/^\d+/.test(lower)) return 2;
            if (lower.includes('house') || lower.includes('place') || lower.includes('&') || lower.includes('and')) return 5;
            return 4;
        };

        return getScore(newName) > getScore(currentName) ? newName : currentName;
    };

    const getAttendees = (excludeRole = 'planner') => {
        const all = PA_GlobalObject?.Planning?.attendees || [];
        return all
            .filter((att) => att.role !== excludeRole && att.role !== 'partial' && att.rsvp !== 'decline')
            .map((att) => ({
                id: att.id,
                cost: 0,
                is_business_expense: false,
                business_expense_amount_authorized: 0,
                business_expense_amount_authorized_by: "",
                business_expense_amount_authorized_timestamp: 0,
                business_expense_amount: 0,
                business_expense_paid: 0,
                business_expense_reference_id: "",
            }));
    };

    // UNIFIED EXTRACTION ENGINE
    const registryData = useMemo(() => {
        const compiled = { lodging: [], meals: [], activities: [], ideas: [] };
        if (!Array.isArray(allEvents)) return compiled;

        const lodgingGroups = new Map();
        const mealGroups = new Map();
        const activityGroups = new Map();
        const ideaGroups = new Map(); // Added map tracker for Ideas

        allEvents.forEach(event => {
            const eventInfo = {
                id: event.id || (event._id && typeof event._id === 'string' ? event._id : ''),
                title: event.title || event.name || "Unnamed Event",
                dateRange: formatEventDateRange(event.time_table)
            };

            // 1. Lodging Extraction
            if (event.lodging) {
                const lodgingItems = Array.isArray(event.lodging) ? event.lodging : [event.lodging];
                lodgingItems.forEach(item => {
                    if (item) {
                        const name = (item.name || "Unnamed Lodging").trim();
                        const streetAddress = (item.address || item.location?.address || "").trim();
                        const city = (item.city || item.location?.city || "").trim();
                        const state = (item.state || item.location?.state || "").trim();
                        const postal = (item.zip || item.location?.zip || "").trim();

                        const lookupKey = normalizeAddressString(`${streetAddress}|${city}|${state}`);
                        const fullLocationString = [streetAddress, city, state, postal].filter(Boolean).join(', ') || "Address Pending";

                        if (!lookupKey && name) {
                            const fallbackKey = normalizeAddressString(name);
                            if (!lodgingGroups.has(fallbackKey)) {
                                lodgingGroups.set(fallbackKey, {
                                    id: `lodging-${eventInfo.id}-${fallbackKey}`,
                                    title: name,
                                    subtitle: "Location Pending",
                                    city: city, state: state, address: "Address Pending",
                                    usedInEvents: [], alternativeNames: [], rawPayload: item
                                });
                            }
                            const entry = lodgingGroups.get(fallbackKey);
                            if (!entry.usedInEvents.some(ev => ev.id === eventInfo.id)) entry.usedInEvents.push(eventInfo);
                            return;
                        }

                        if (!lodgingGroups.has(lookupKey)) {
                            lodgingGroups.set(lookupKey, {
                                id: `lodging-${eventInfo.id}-${lookupKey}`,
                                title: name,
                                subtitle: city && state ? `${city}, ${state}` : city || state || "Location Pending",
                                city: city,
                                state: state,
                                address: fullLocationString,
                                usedInEvents: [],
                                alternativeNames: new Set(),
                                rawPayload: item
                            });
                        }

                        const existingEntry = lodgingGroups.get(lookupKey);
                        existingEntry.title = getBetterName(existingEntry.title, name);
                        if (name && name !== existingEntry.title) existingEntry.alternativeNames.add(name);

                        const alreadyLinked = existingEntry.usedInEvents.some(ev => ev.id === eventInfo.id);
                        if (!alreadyLinked) existingEntry.usedInEvents.push(eventInfo);
                    }
                });
            }

            // 2. Meals Extraction
            if (Array.isArray(event.meals)) {
                event.meals.forEach(item => {
                    if (item && (item.title || item.name)) {
                        const titleName = (item.title || item.name || "Planned Meal").trim();
                        const locName = (item.location?.name || item.name || "").trim();
                        const streetAddress = (item.location?.address || item.address || "").trim();
                        const city = (item.location?.city || item.city || "").trim();
                        const state = (item.location?.state || item.state || "").trim();
                        const postal = (item.location?.zip || item.zip || "").trim();

                        if (!locName && !streetAddress) return;

                        const fullAddressString = [streetAddress, city, state, postal].filter(Boolean).join(', ') || "Address Pending";
                        const mealLookupKey = streetAddress ? normalizeAddressString(`${streetAddress}|${city}|${state}`) : normalizeAddressString(`${locName}`);

                        if (!mealGroups.has(mealLookupKey)) {
                            mealGroups.set(mealLookupKey, {
                                id: `meal-${eventInfo.id}-${mealLookupKey}`,
                                title: locName || titleName,
                                subtitle: titleName !== (locName || titleName) ? titleName : "Meal Location",
                                city: city,
                                state: state,
                                address: fullAddressString,
                                usedInEvents: [],
                                alternativeNames: new Set(),
                                rawPayload: item
                            });
                        }

                        const existingMeal = mealGroups.get(mealLookupKey);
                        existingMeal.title = getBetterName(existingMeal.title, locName || titleName);

                        const alreadyLinked = existingMeal.usedInEvents.some(ev => ev.id === eventInfo.id);
                        if (!alreadyLinked) existingMeal.usedInEvents.push(eventInfo);
                    }
                });
            }

            // 3. Activities Extraction
            if (Array.isArray(event.activities)) {
                event.activities.forEach(item => {
                    if (item && (item.title || item.name)) {
                        const titleName = (item.title || item.name || "Planned Activity").trim();
                        const locName = (item.location?.name || item.location_name || "").trim();
                        const streetAddress = (item.location?.address || item.address || "").trim();
                        const city = (item.location?.city || item.city || "").trim();
                        const state = (item.location?.state || item.state || "").trim();
                        const postal = (item.location?.zip || item.zip || "").trim();

                        if (!locName && !streetAddress) return;

                        const fullAddressString = [streetAddress, city, state, postal].filter(Boolean).join(', ') || "Address Pending";
                        const activityLookupKey = streetAddress ? normalizeAddressString(`${streetAddress}|${city}|${state}`) : normalizeAddressString(`${locName}`);

                        if (!activityGroups.has(activityLookupKey)) {
                            activityGroups.set(activityLookupKey, {
                                id: `activity-${eventInfo.id}-${activityLookupKey}`,
                                title: locName || titleName,
                                subtitle: titleName !== (locName || titleName) ? titleName : "Activity Location",
                                city: city,
                                state: state,
                                address: fullAddressString,
                                usedInEvents: [],
                                alternativeNames: new Set(),
                                rawPayload: item
                            });
                        }

                        const existingActivity = activityGroups.get(activityLookupKey);
                        existingActivity.title = getBetterName(existingActivity.title, locName || titleName);

                        const alreadyLinked = existingActivity.usedInEvents.some(ev => ev.id === eventInfo.id);
                        if (!alreadyLinked) existingActivity.usedInEvents.push(eventInfo);
                    }
                });
            }

            // 4. Ideas Extraction Fixed to build complete address maps
            if (Array.isArray(event.ideas)) {
                event.ideas.forEach(item => {
                    if (item && (item.title || item.name)) {
                        const titleName = (item.title || item.name || "Idea Blueprint").trim();
                        const streetAddress = (item.location?.address || item.address || "").trim();
                        const city = (item.location?.city || item.city || "").trim();
                        const state = (item.location?.state || item.state || "").trim();
                        const postal = (item.location?.zip || item.zip || "").trim();

                        const fullAddressString = [streetAddress, city, state, postal].filter(Boolean).join(', ') || item.location?.formatted_address || "Address Pending";
                        const ideaLookupKey = streetAddress ? normalizeAddressString(`${streetAddress}|${city}|${state}`) : normalizeAddressString(`${titleName}`);

                        if (!ideaGroups.has(ideaLookupKey)) {
                            ideaGroups.set(ideaLookupKey, {
                                id: `idea-${eventInfo.id}-${ideaLookupKey}`,
                                title: titleName,
                                subtitle: item.note || "Brainstorm Note",
                                city: city,
                                state: state,
                                address: fullAddressString,
                                usedInEvents: [],
                                alternativeNames: new Set(),
                                rawPayload: item
                            });
                        }

                        const existingIdea = ideaGroups.get(ideaLookupKey);
                        const alreadyLinked = existingIdea.usedInEvents.some(ev => ev.id === eventInfo.id);
                        if (!alreadyLinked) existingIdea.usedInEvents.push(eventInfo);
                    }
                });
            }
        });

        compiled.lodging = Array.from(lodgingGroups.values()).map(el => ({...el, alternativeNames: Array.from(el.alternativeNames || [])}));
        compiled.meals = Array.from(mealGroups.values()).map(el => ({...el, alternativeNames: Array.from(el.alternativeNames || [])}));
        compiled.activities = Array.from(activityGroups.values()).map(el => ({...el, alternativeNames: Array.from(el.alternativeNames || [])}));
        compiled.ideas = Array.from(ideaGroups.values()).map(el => ({...el, alternativeNames: Array.from(el.alternativeNames || [])}));

        Object.keys(compiled).forEach(key => {
            compiled[key].sort((a, b) => a.title.localeCompare(b.title));
        });

        return compiled;
    }, [allEvents]);

    // CHIP FILTERS CONFIGURATION
    const locationFacetHierarchy = useMemo(() => {
        const activeCategory = tabKeys[currentTab];
        const items = registryData[activeCategory] || [];
        const hierarchy = {};

        items.forEach(item => {
            if (item.state && item.city) {
                const stateKey = item.state.trim().toUpperCase();
                const cityValue = item.city.trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

                if (!hierarchy[stateKey]) {
                    hierarchy[stateKey] = new Set();
                }
                hierarchy[stateKey].add(cityValue);
            }
        });

        const sortedStates = Object.keys(hierarchy).sort((a, b) => a.localeCompare(b));
        return sortedStates.map(state => ({
            state,
            cities: Array.from(hierarchy[state]).sort((a, b) => a.localeCompare(b))
        }));
    }, [registryData, currentTab]);

    const filteredItems = useMemo(() => {
        const activeCategory = tabKeys[currentTab];
        let items = registryData[activeCategory] || [];

        if (selectedStateFacet) {
            items = items.filter(item => item.state && item.state.toLowerCase() === selectedStateFacet.toLowerCase());
        }
        if (selectedCityFacet) {
            items = items.filter(item => item.city && item.city.toLowerCase() === selectedCityFacet.toLowerCase());
        }

        const query = searchQuery.toLowerCase().trim();
        if (!query) return items;

        return items.filter(item =>
            item.title.toLowerCase().includes(query) ||
            (item.subtitle && item.subtitle.toLowerCase().includes(query)) ||
            (item.address && item.address.toLowerCase().includes(query)) ||
            (item.alternativeNames && item.alternativeNames.some(alt => alt.toLowerCase().includes(query))) ||
            (item.usedInEvents && item.usedInEvents.some(ev => ev.title.toLowerCase().includes(query)))
        );
    }, [registryData, currentTab, searchQuery, selectedStateFacet, selectedCityFacet]);

    // REACTION ACTIONS HANDLER
    const handleAddItemToPlanner = (targetItem) => {
        if (!PA_GlobalObject?.Planning) {
            alert("No active trip blueprint loaded in the planner window.");
            return;
        }

        const activeCategory = tabKeys[currentTab];
        const currentPlanning = { ...PA_GlobalObject.Planning };
        const payload = targetItem.rawPayload;

        const currentStatus = currentPlanning.status;
        const eventId = currentPlanning.id;

        const safeClone = (obj) => {
            if (!obj) return obj;
            return JSON.parse(JSON.stringify(obj));
        };

        switch (activeCategory) {
            case 'lodging': {
                if (!Array.isArray(currentPlanning.lodging)) currentPlanning.lodging = [];

                const _new_lodg = new Lodging();
                _new_lodg.name = payload.displayName?.text || payload.displayName || payload.name || targetItem.title;
                _new_lodg.attendees = getAttendees('planner');

                const eventTT = currentPlanning.time_table;
                if (eventTT?.dates?.length > 0 && typeof createTimeTableForLodging === 'function') {
                    _new_lodg.time_table = createTimeTableForLodging(
                        eventTT.dates[0].date,
                        eventTT.dates[eventTT.dates.length - 1].date,
                        eventTT
                    );
                }

                _new_lodg.Place = safeClone(payload.Place || payload);
                _new_lodg.location = safeClone(payload.location);
                _new_lodg.photos = safeClone(payload.photos || []);

                currentPlanning.lodging.push(_new_lodg);
                set_PA_GlobalObject({ Planning: currentPlanning });

                if (currentStatus !== 10 && typeof sendSocketMessage === 'function') {
                    sendSocketMessage({ action: "E_addEventLodging_v2", event_id: eventId, lodging: _new_lodg });
                }
                break;
            }

            case 'meals': {
                if (!Array.isArray(currentPlanning.meals)) currentPlanning.meals = [];

                const _new_meal = new Meal();
                _new_meal.title = payload.displayName?.text || payload.displayName || payload.title || targetItem.title;
                _new_meal.attendees = getAttendees('planner');

                _new_meal.Place = safeClone(payload.Place || payload);
                _new_meal.location = safeClone(payload.location);
                _new_meal.photos = safeClone(payload.photos || []);

                currentPlanning.meals.push(_new_meal);
                set_PA_GlobalObject({ Planning: currentPlanning });

                if (currentStatus !== 10 && typeof sendSocketMessage === 'function') {
                    sendSocketMessage({ action: "E_addEventMeal_v2", event_id: eventId, meal: _new_meal });
                }
                break;
            }

            case 'activities': {
                if (!Array.isArray(currentPlanning.activities)) currentPlanning.activities = [];

                const _new_act = new Activity();
                _new_act.title = payload.displayName?.text || payload.displayName || payload.title || targetItem.title;
                _new_act.attendees = getAttendees('planner');

                _new_act.Place = safeClone(payload.Place || payload);
                _new_act.location = safeClone(payload.location);
                _new_act.photos = safeClone(payload.photos || []);

                currentPlanning.activities.push(_new_act);
                set_PA_GlobalObject({ Planning: currentPlanning });

                if (currentStatus !== 10 && typeof sendSocketMessage === 'function') {
                    sendSocketMessage({ action: "E_addEventActivity_v2", event_id: eventId, activity: _new_act });
                }
                break;
            }

            case 'ideas': {
                if (!Array.isArray(currentPlanning.ideas)) currentPlanning.ideas = [];

                const _new_idea = new Idea();
                _new_idea.title = payload.title || payload.name || payload.displayName?.text || targetItem.title;
                _new_idea.category = payload.category || "";
                _new_idea.status = 0;

                if (typeof Account !== 'undefined' && Account?.id) {
                    _new_idea.added_by = Account.id;
                    _new_idea.created_by = Account.id;
                } else {
                    _new_idea.added_by = payload.added_by || "";
                    _new_idea.created_by = payload.created_by || "";
                }
                _new_idea.created = Date.now();

                _new_idea.location = safeClone(payload.location);
                _new_idea.Place = safeClone(payload.Place);
                _new_idea.photos = safeClone(payload.photos || []);
                _new_idea.types = safeClone(payload.types || []);
                _new_idea.comments = [];

                _new_idea.rating = payload.rating;
                _new_idea.total_ratings = payload.total_ratings;
                _new_idea.phone = payload.phone || "";
                _new_idea.website = payload.website || "";
                _new_idea.google_place_id = payload.google_place_id || payload.Place?.place_id || "";

                currentPlanning.ideas.push(_new_idea);
                set_PA_GlobalObject({ Planning: currentPlanning });

                if (currentStatus !== 10 && typeof sendSocketMessage === 'function') {
                    sendSocketMessage({ action: "E_addEventIdea_v2", event_id: eventId, idea: _new_idea });
                }
                break;
            }
            default:
                break;
        }
    };

    const activeAvailableCities = useMemo(() => {
        if (!selectedStateFacet) return [];
        const match = locationFacetHierarchy.find(h => h.state === selectedStateFacet);
        return match ? match.cities : [];
    }, [selectedStateFacet, locationFacetHierarchy]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: { background: '#ffffff', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', height: '85vh', display: 'flex', flexDirection: 'column' }
            }}
        >
            <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                <Box>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                        Master Registry Lists
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                        Browse, cross-reference, and inject historical items into your loaded planning workbook.
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: '#94a3b8', '&:hover': { color: '#0f172a', background: '#f1f5f9' } }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box sx={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ borderBottom: '1px solid #e2e8f0', px: 1 }}>
                    <Tabs
                        value={currentTab}
                        onChange={(e, nv) => { setCurrentTab(nv); setSearchQuery(''); setSelectedStateFacet(null); setSelectedCityFacet(null); }}
                        variant="fullWidth"
                        sx={{
                            '& .MuiTabs-indicator': { height: '3px', borderRadius: '3px', background: '#2563eb' },
                            '& .MuiTab-root': { fontWeight: 700, fontSize: '0.9rem', textTransform: 'none', py: 2, color: '#64748b', '&.Mui-selected': { color: '#2563eb' } }
                        }}
                    >
                        <Tab icon={<HotelIcon sx={{ fontSize: '1.2rem' }} />} iconPosition="start" label={<Badge badgeContent={registryData.lodging.length} color="primary" sx={{ '& .MuiBadge-badge': { right: -12, fontSize: '0.65rem' } }}>Lodging</Badge>} />
                        <Tab icon={<RestaurantIcon sx={{ fontSize: '1.2rem' }} />} iconPosition="start" label={<Badge badgeContent={registryData.meals.length} color="primary" sx={{ '& .MuiBadge-badge': { right: -12, fontSize: '0.65rem' } }}>Meals</Badge>} />
                        <Tab icon={<LocalActivityIcon sx={{ fontSize: '1.2rem' }} />} iconPosition="start" label={<Badge badgeContent={registryData.activities.length} color="primary" sx={{ '& .MuiBadge-badge': { right: -12, fontSize: '0.65rem' } }}>Activities</Badge>} />
                        <Tab icon={<LightbulbIcon sx={{ fontSize: '1.2rem' }} />} iconPosition="start" label={<Badge badgeContent={registryData.ideas.length} color="primary" sx={{ '& .MuiBadge-badge': { right: -12, fontSize: '0.65rem' } }}>Ideas</Badge>} />
                    </Tabs>
                </Box>

                {locationFacetHierarchy.length > 0 && (
                    <Box sx={{ p: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, borderBottom: '1px dashed #e2e8f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '95px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                <MapIcon sx={{ fontSize: '0.95rem', color: '#475569' }} /> By State:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.8, overflowX: 'auto', flexGrow: 1, py: 0.5, px: 0.2, whiteSpace: 'nowrap', '&::-webkit-scrollbar': { height: '5px' }, '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '4px' } }}>
                                <Chip label="All States" size="small" clickable onClick={() => { setSelectedStateFacet(null); setSelectedCityFacet(null); }} variant={selectedStateFacet === null ? "filled" : "outlined"} sx={{ fontWeight: 700, flexShrink: 0, px: 0.5, color: selectedStateFacet === null ? '#ffffff' : '#475569', background: selectedStateFacet === null ? '#0f172a' : 'transparent' }} />
                                {locationFacetHierarchy.map((node) => (
                                    <Chip key={node.state} label={node.state} size="small" clickable onClick={() => { setSelectedStateFacet(node.state); setSelectedCityFacet(null); }} variant={selectedStateFacet === node.state ? "filled" : "outlined"} sx={{ fontWeight: 700, flexShrink: 0, color: selectedStateFacet === node.state ? '#ffffff' : '#475569', background: selectedStateFacet === node.state ? '#2563eb' : '#ffffff', borderColor: '#cbd5e1' }} />
                                ))}
                            </Box>
                        </Box>

                        {selectedStateFacet && activeAvailableCities.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 1, borderLeft: '2px solid #cbd5e1', minWidth: 0, animation: 'fadeIn 0.2s ease' }}>
                                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '95px', flexShrink: 0 }}>
                                    <LocationOnIcon sx={{ fontSize: '0.95rem', color: '#64748b' }} /> Cities ({selectedStateFacet}):
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.8, overflowX: 'auto', flexGrow: 1, py: 0.5, px: 0.2, whiteSpace: 'nowrap', '&::-webkit-scrollbar': { height: '5px' }, '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '4px' } }}>
                                    <Chip label="All Cities" size="small" clickable onClick={() => setSelectedCityFacet(null)} variant={selectedCityFacet === null ? "filled" : "outlined"} sx={{ fontWeight: 600, flexShrink: 0, fontSize: '0.8rem', color: selectedCityFacet === null ? '#ffffff' : '#475569', background: selectedCityFacet === null ? '#475569' : 'transparent' }} />
                                    {activeAvailableCities.map((city) => (
                                        <Chip key={city} label={city} size="small" clickable onClick={() => setSelectedCityFacet(city)} variant={selectedCityFacet === city ? "filled" : "outlined"} sx={{ fontWeight: 600, flexShrink: 0, fontSize: '0.8rem', color: selectedCityFacet === city ? '#ffffff' : '#475569', background: selectedCityFacet === city ? '#16a34a' : '#ffffff', borderColor: '#cbd5e1' }} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <TextField size="small" placeholder="Type keyword, street name, or alias name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: '1.1rem' }} /></InputAdornment> }} sx={{ width: { xs: '100%', sm: '360px' }, '& .MuiOutlinedInput-root': { background: '#ffffff', borderRadius: '12px', '& fieldset': { borderColor: '#cbd5e1' } } }} />
                </Box>
            </Box>

            <DialogContent sx={{ p: 3, background: '#f8fafc', flexGrow: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '4px' } }}>
                {filteredItems.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed #cbd5e1', borderRadius: '16px', background: '#ffffff' }}>
                        <Typography variant="subtitle1" sx={{ color: '#64748b', fontWeight: 600 }}>No master matching configurations found inside this category filter scope.</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {filteredItems.map((item) => (
                            <Card key={item.id} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', background: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.01)', transition: 'all 0.2s ease', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 12px rgba(0,0,0,0.04)', borderColor: '#cbd5e1' } }}>
                                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', mb: 0.5 }}>{item.title}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                                            {item.subtitle && (
                                                <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', mb: 0.2 }}>
                                                    Ref: {item.subtitle}
                                                </Typography>
                                            )}

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#475569' }}>
                                                <HomeWorkIcon sx={{ fontSize: '0.95rem', color: '#64748b' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>{item.address}</Typography>
                                            </Box>

                                            {item.alternativeNames?.length > 0 && (
                                                <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic', mt: 0.5 }}>Also known as: {item.alternativeNames.join(', ')}</Typography>
                                            )}

                                            <Tooltip arrow placement="right" componentsProps={{ tooltip: { sx: { bgcolor: '#0f172a', borderRadius: '12px', p: 0, boxShadow: '0 10px 25px rgba(0,0,0,0.2)', border: '1px solid #334155', maxWidth: '320px' } } }} title={
                                                <Box sx={{ p: 2 }}>
                                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700, color: '#94a3b8', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}><HistoryIcon sx={{ fontSize: '0.85rem' }} /> Trip Timeline Usage History</Typography>
                                                    <Divider sx={{ borderColor: '#334155', mb: 1 }} />
                                                    <Box sx={{ maxHeight: '240px', overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#475569', borderRadius: '4px' } }}>
                                                        {item.usedInEvents.map((ev, i) => (
                                                            <Box key={i} sx={{ my: 1.2, '&:first-of-type': { mt: 0 } }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>• {ev.title}</Typography>
                                                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', pl: 1.5, mt: 0.2 }}>{ev.dateRange}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            }>
                                                <Chip icon={<HistoryIcon sx={{ fontSize: '0.85rem !important' }} />} label={`Used in ${item.usedInEvents.length} Event${item.usedInEvents.length === 1 ? '' : 's'}`} size="small" clickable sx={{ alignSelf: 'flex-start', mt: 0.5, fontWeight: 600, background: currentTab === 0 ? '#eff6ff' : currentTab === 1 ? '#fff7ed' : currentTab === 2 ? '#f0fdf4' : '#faf5ff', color: currentTab === 0 ? '#2563eb' : currentTab === 1 ? '#ea580c' : currentTab === 2 ? '#16a34a' : '#9333ea', border: currentTab === 0 ? '1px solid #bfdbfe' : currentTab === 1 ? '1px solid #ffedd5' : currentTab === 2 ? '1px solid #bbf7d0' : '1px solid #e9d5ff' }} />
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    <Button variant="outlined" size="medium" onClick={() => handleAddItemToPlanner(item)} startIcon={<AddCircleOutlineIcon />} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', borderColor: '#2563eb', color: '#2563eb', whiteSpace: 'nowrap', px: 2.5, '&:hover': { background: '#eff6ff', borderColor: '#1d4ed8' } }}>Add to Planner</Button>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}